import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppDispatch } from 'core/store';
import { HTTPService } from 'core/services';
import periodMapping from 'shared/PeriodAutocomplete/constants';
import { useActionsPermissions } from 'core/hooks/useActionsPermissions';
import calculatePeriod from 'core/utilities/dateUtilities/calculatePeriod';
import { notifierMessage } from '@dashboardWirelineInsideSalesDepartment/constants';
import { PERIOD_SELECT } from 'shared/PeriodAutocomplete/PeriodAutocomplete.models';
import { TPeriod, TSetPeriod } from 'shared/CustomRangePicker/CustomRangePicker.models';
import { ACTIONS_DASHBOARD_WIRELINE_INSIDE_SALES_PATHS } from 'core/models/actionsPermissions';
import { getRequestStatusAction } from '@dashboardWirelineInsideSalesDepartment/GetRequestStatus';
import { getInsideSalesUsersAction } from '@dashboardWirelineInsideSalesDepartment/GetInsideSalesUsers';

import useNotifiers from 'shared/Notifier/UseNotifiers';

const {
  BY_USERS_CHART_GET,
  REQUEST_STATUS_CHART_GET,
  REQUEST_BOOKING_TABLE_GET,
} = ACTIONS_DASHBOARD_WIRELINE_INSIDE_SALES_PATHS;

const actionsPaths = {
  isAvailableByUsersChart: BY_USERS_CHART_GET,
  isAvailableRequestStatusChart: REQUEST_STATUS_CHART_GET,
  isAvailableRequestBookingTable: REQUEST_BOOKING_TABLE_GET,
};

interface IActionsPermissions {
  isAvailableByUsersChart: boolean,
  isAvailableRequestStatusChart: boolean,
  isAvailableRequestBookingTable: boolean,
}
interface IInsideSalesDepartmentContext {
  getDashboardData: TSetPeriod;
  period: TPeriod;
  actionsPermissions: IActionsPermissions;
}

interface IInsideSalesDepartmentProviderProps {
  children: React.ReactNode;
}

const InsideSalesDepartmentContext = createContext<
  IInsideSalesDepartmentContext
>({} as IInsideSalesDepartmentContext);

let requestStatusController = HTTPService.getController();
let insideSalesUsersController = HTTPService.getController();

const InsideSalesDepartmentProvider: React.FC<IInsideSalesDepartmentProviderProps> = ({
  children,
}) => {
  const defaultPeriod = calculatePeriod(periodMapping, PERIOD_SELECT.ONE_MONTH);
  const [period, setPeriod] = useState(defaultPeriod);
  const actionsPermissions = useActionsPermissions<IActionsPermissions>(actionsPaths);

  const {
    isAvailableByUsersChart,
    isAvailableRequestStatusChart,
  } = actionsPermissions;

  const { showErrorNotifier } = useNotifiers();

  const dispatch = useAppDispatch();

  const cancelGetRequestStatusRequest = () => {
    HTTPService.cancelRequest(requestStatusController);
  };

  const canselGetInsideSalesUsersRequest = () => {
    HTTPService.cancelRequest(insideSalesUsersController);
  };

  const getRequestStatus = async (newPeriod: TPeriod) => {
    if (!isAvailableRequestStatusChart) return;

    cancelGetRequestStatusRequest();
    requestStatusController = HTTPService.getController();
    try {
      await dispatch(getRequestStatusAction({
        controller: requestStatusController,
        params: newPeriod,
      })).unwrap();
    } catch (e) {
      showErrorNotifier(notifierMessage.requestStatus.error, e);
    }
  };

  const getInsideSalesUsers = async (newPeriod: TPeriod) => {
    if (!isAvailableByUsersChart) return;

    canselGetInsideSalesUsersRequest();
    insideSalesUsersController = HTTPService.getController();
    try {
      await dispatch(getInsideSalesUsersAction({
        controller: insideSalesUsersController,
        params: newPeriod,
      })).unwrap();
    } catch (e) {
      showErrorNotifier(notifierMessage.insideSalesUsers.error, e);
    }
  };

  const getDashboardData = (newPeriod: TPeriod) => {
    getRequestStatus(newPeriod);
    getInsideSalesUsers(newPeriod);
    setPeriod(newPeriod);
  };

  const cancelRequests = () => {
    cancelGetRequestStatusRequest();
    canselGetInsideSalesUsersRequest();
  };

  useEffect(() => {
    getDashboardData(period);
    return () => cancelRequests();
  }, [period]);

  const value = useMemo(() => ({
    getDashboardData,
    period,
    actionsPermissions,
  }), [period, actionsPermissions]);

  return (
    <InsideSalesDepartmentContext.Provider value={value}>
      {children}
    </InsideSalesDepartmentContext.Provider>
  );
};

export const useInsideSalesDepartmentContext = () => useContext(InsideSalesDepartmentContext);

export default InsideSalesDepartmentProvider;
